/**
 * 父格（left/top）推导。
 *
 * 策略（与样本实测一致）：默认 'default'，让引擎自动按最近扩展格推导；
 * 仅当单元格显式给出 left/top（且非 'default'）时采用。交叉表取值/小计格等
 * 需要打破默认推导的场景，由 builder/用户显式指定坐标。
 *
 * 同时提供 nearest-expanding 自动推导函数（可选启用）与成环检测。
 */

import type { CellIR } from '../ir/types.js';
import { coordName, parseCoord } from './coordinate.js';

export interface ParentedCell extends CellIR {
  resolvedLeft: string;
  resolvedTop: string;
}

/** 解析单个单元格的父格：显式优先，否则 'default'。 */
export function resolveParent(cell: CellIR): { left: string; top: string } {
  const left = cell.left && cell.left !== 'default' ? cell.left : 'default';
  const top = cell.top && cell.top !== 'default' ? cell.top : 'default';
  return { left, top };
}

/**
 * 自动推导父格：同列上方最近的 expand='down' 格为 top 父格；
 * 同行左侧最近的 expand='right' 格为 left 父格；未命中则 'default'。
 * 仅在 builder 显式调用时启用。
 */
export function deriveAutoParents(cells: CellIR[]): ParentedCell[] {
  const sorted = [...cells].sort((a, b) => a.row - b.row || a.col - b.col);
  return sorted.map((cell) => {
    let top = 'default';
    let left = 'default';
    if (cell.expand === 'down' || cell.expand === 'none') {
      for (let r = cell.row - 1; r >= 1; r--) {
        const cand = sorted.find((c) => c.row === r && c.col === cell.col);
        if (cand && cand.expand === 'down') {
          top = coordName(cand.row, cand.col);
          break;
        }
      }
    }
    if (cell.expand === 'right' || cell.expand === 'none') {
      for (let c = cell.col - 1; c >= 1; c--) {
        const cand = sorted.find((x) => x.row === cell.row && x.col === c);
        if (cand && cand.expand === 'right') {
          left = coordName(cand.row, cand.col);
          break;
        }
      }
    }
    return {
      ...cell,
      resolvedLeft: cell.left && cell.left !== 'default' ? cell.left : left,
      resolvedTop: cell.top && cell.top !== 'default' ? cell.top : top,
    };
  });
}

/** 顶层父格信息。 */
export interface TopParentInfo {
  /** 顶层左父格（向下扩展树的根），或 null。 */
  left: { name: string; row: number; col: number } | null;
  /** 顶层上父格（向右扩展树的根），或 null。 */
  top: { name: string; row: number; col: number } | null;
}

/**
 * 定位顶层父格码：在全部单元格中，分别找出
 *  - 顶层左父格：expand='down' 的格中 row 最小者；若并列则取 col 最小者；
 *  - 顶层上父格：expand='right' 的格中 col 最小者；若并列则取 row 最小者。
 * 父表数据行号由数据本身决定，不依赖任何配置/硬编码。
 */
export function findTopParentCells(cells: CellIR[]): TopParentInfo {
  const byCoord = new Map<string, CellIR>();
  for (const c of cells) byCoord.set(coordName(c.row, c.col), c);

  let left: { name: string; row: number; col: number } | null = null;
  let top: { name: string; row: number; col: number } | null = null;

  for (const c of cells) {
    if (c.expand !== 'down') {
      // 仍要检查顶层上父格
    } else {
      const name = coordName(c.row, c.col);
      if (!left || c.row < left.row || (c.row === left.row && c.col < left.col)) {
        left = { name, row: c.row, col: c.col };
      }
    }
    if (c.expand === 'right') {
      const name = coordName(c.row, c.col);
      if (!top || c.col < top.col || (c.col === top.col && c.row < top.row)) {
        top = { name, row: c.row, col: c.col };
      }
    }
  }
  void byCoord;
  return { left, top };
}

/**
 * 规范化顶层父格码：双向断开避免左父格 / 上父格交叉成环。
 *
 * 规则：
 *  1. 顶层左父格自身 left 必须为 'none'；若它不在第 1 行（上方还有单元格），
 *     则其 top 也补为 'none'，断开纵向回溯，避免 A.left=B 且 B.top=A 的交叉环。
 *  2. 顶层上父格自身 top 必须为 'none'；若它不在第 1 列（左侧还有单元格），
 *     则其 left 也补为 'none'，对称断开横向回溯。
 *
 * 返回新的单元格数组（不修改入参）。
 */
export function normalizeTopParentCells(cells: CellIR[]): CellIR[] {
  const { left, top } = findTopParentCells(cells);
  return cells.map((c) => {
    const name = coordName(c.row, c.col);
    if (left && name === left.name) {
      const patch: Partial<CellIR> = { left: 'none' };
      if (left.row > 1) patch.top = 'none';
      return { ...c, ...patch };
    }
    if (top && name === top.name) {
      const patch: Partial<CellIR> = { top: 'none' };
      if (top.col > 1) patch.left = 'none';
      return { ...c, ...patch };
    }
    return c;
  });
}

/**
 * 检测左父格、上父格间的交叉循环依赖。
 *
 * 以坐标（坐标名）为追踪对象，不再依赖 cell.name 字段：
 *  沿 A 的 left 链前进到 B，若 B 的 top 链（上父格链）中出现 A，则
 *  A 与 B 构成「左父格、上父格间循环依赖」，返回可定位的中文报错。
 * 对称地，沿 A 的 top 链前进到 B，检查 B 的 left 链是否含 A。
 */
export function detectParentCycle(cells: CellIR[]): string | null {
  const byName = new Map<string, CellIR>();
  for (const c of cells) byName.set(coordName(c.row, c.col), c);

  const resolveTo = (
    start: CellIR,
    dir: 'left' | 'top',
    against: 'left' | 'top',
  ): string | null => {
    const seen = new Set<string>();
    let cur: CellIR | undefined = start;
    while (cur) {
      const name = coordName(cur.row, cur.col);
      if (seen.has(name)) break; // 已出现，纯 left/纯 top 自环由上层处理
      seen.add(name);
      // 沿 dir 链前进
      const nextName = dir === 'left' ? cur.left : cur.top;
      if (!nextName || nextName === 'default' || nextName === 'none') break;
      const next = byName.get(nextName);
      if (!next) break;
      // 检查 next 的 against 链是否回到 start
      const againstSeen = new Set<string>();
      let a: CellIR | undefined = next;
      while (a) {
        const aName = coordName(a.row, a.col);
        if (againstSeen.has(aName)) break;
        againstSeen.add(aName);
        if (aName === coordName(start.row, start.col)) {
          return `单元格[${nextName}]和[${name}]左父格、上父格间存在循环依赖`;
        }
        const aNext = against === 'left' ? a.left : a.top;
        if (!aNext || aNext === 'default' || aNext === 'none') break;
        a = byName.get(aNext);
      }
      cur = next;
    }
    return null;
  };

  for (const start of cells) {
    const msg = resolveTo(start, 'left', 'top') ?? resolveTo(start, 'top', 'left');
    if (msg) return msg;
  }
  return null;
}
